import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Species } from '../types/gql-op-types';

const SPECIES_URL_PARAM = 'type';
const SPECIES_STORAGE_KEY = 'fi-species';

export function useSpeciesUrlParam(defaultSpecies: Species = Species.DOG) {
  const history = useHistory();
  const location = useLocation();

  // Get species from URL, localStorage, or use default species
  const getSpeciesFromUrl = (): Species => {
    const params = new URLSearchParams(location.search);
    const speciesParam = params.get(SPECIES_URL_PARAM);
    if (speciesParam === 'dog') return Species.DOG;
    if (speciesParam === 'cat') return Species.CAT;

    // Check localStorage if no URL param
    const cached = localStorage.getItem(SPECIES_STORAGE_KEY);
    if (cached === 'dog') return Species.DOG;
    if (cached === 'cat') return Species.CAT;

    return defaultSpecies;
  };

  const [species, setSpeciesState] = useState<Species>(getSpeciesFromUrl());

  // Set URL parameter on initial load if missing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const speciesParam = params.get(SPECIES_URL_PARAM);

    if (!speciesParam) {
      params.set(SPECIES_URL_PARAM, species === Species.DOG ? 'dog' : 'cat');
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  }, [history, location.pathname, location.search, species]);

  // Update local species state when URL param changes externally
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const speciesParam = params.get(SPECIES_URL_PARAM);

    let urlSpecies: Species | undefined;
    if (speciesParam === 'dog') urlSpecies = Species.DOG;
    if (speciesParam === 'cat') urlSpecies = Species.CAT;

    if (urlSpecies && urlSpecies !== species) {
      setSpeciesState(urlSpecies);
      localStorage.setItem(SPECIES_STORAGE_KEY, speciesParam!);
    }
  }, [location.search, species]);

  const setSpecies = (newSpecies: Species) => {
    setSpeciesState(newSpecies);

    const speciesValue = newSpecies === Species.DOG ? 'dog' : 'cat';
    localStorage.setItem(SPECIES_STORAGE_KEY, speciesValue);

    const params = new URLSearchParams(location.search);
    params.set(SPECIES_URL_PARAM, speciesValue);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  return [species, setSpecies] as const;
}
